import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'oidc-react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  TablePagination,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import { Tooltip } from '@agronod/mui-components';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Settings as SettingsIcon,
  Build as BuildIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { AdminService } from '@/services/admin.service';
import { ControlType, Issuer, UpdateControlTypeRequest, CloneControlTypeRequest } from '@/types';
import { useSaveFormConfiguration, type SaveFormConfigurationInput } from '@/hooks/useSaveFormConfiguration';
import { useSnackbar } from 'notistack';
import { useApiClient } from '@/hooks/useApiClient';
import CreateControlTypeModal from '@/components/admin/CreateControlTypeModal';
import EditControlTypeModal from '@/components/admin/EditControlTypeModal';
import CloneControlTypeModal from '@/components/admin/CloneControlTypeModal';
import FormConfigurationBuilder from '@/components/admin/FormConfigurationBuilder';

const AdminControlTypesPage: React.FC = () => {
  useApiClient(); // Inject current OIDC token into API client
  const { userData } = useAuth(); // Get OIDC authentication state
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    includeInactive: false,
    issuerId: '',
    page: 1,
    limit: 10,
  });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [isFormConfigModalOpen, setIsFormConfigModalOpen] = useState(false);
  const [selectedControlType, setSelectedControlType] = useState<ControlType | null>(null);
  const [issuers, setIssuers] = useState<Issuer[]>([]);

  // Fetch issuers for filter dropdown - only when authenticated
  useEffect(() => {
    if (!userData?.access_token) return;

    AdminService.getIssuers()
      .then(response => setIssuers(response.issuers))
      .catch(error => console.error('Failed to load issuers:', error));
  }, [userData?.access_token]);

  // Fetch control types - only when authenticated
  const {
    data: controlTypesData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['admin', 'control-types', filters],
    queryFn: () => AdminService.getControlTypes(filters),
    enabled: !!userData?.access_token, // Wait for OIDC token before making request
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: AdminService.deleteControlType,
    onSuccess: () => {
      enqueueSnackbar('Kontrolltyp raderad', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'control-types'] });
    },
    onError: (error: any) => {
      enqueueSnackbar(error.message || 'Fel vid radering', { variant: 'error' });
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: AdminService.createControlType,
    onSuccess: () => {
      enqueueSnackbar('Kontrolltyp skapad', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'control-types'] });
      setIsCreateModalOpen(false);
    },
    onError: (error: any) => {
      enqueueSnackbar(error.message || 'Fel vid skapande av kontrolltyp', { variant: 'error' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateControlTypeRequest }) =>
      AdminService.updateControlType(id, data),
    onSuccess: () => {
      enqueueSnackbar('Kontrolltyp uppdaterad', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'control-types'] });
      setIsEditModalOpen(false);
      setSelectedControlType(null);
    },
    onError: (error: any) => {
      enqueueSnackbar(error.message || 'Fel vid uppdatering av kontrolltyp', { variant: 'error' });
    },
  });

  // Clone mutation
  const cloneMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CloneControlTypeRequest }) =>
      AdminService.cloneControlType(id, data),
    onSuccess: () => {
      enqueueSnackbar('Kontrolltyp duplicerad', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'control-types'] });
      setIsCloneModalOpen(false);
      setSelectedControlType(null);
    },
    onError: (error: any) => {
      enqueueSnackbar(error.message || 'Fel vid duplicering av kontrolltyp', { variant: 'error' });
    },
  });

  // Writes form configuration and requirement order as one unit; the builder
  // decides for itself whether to stay open afterwards.
  const saveFormConfiguration = useSaveFormConfiguration();

  // Handle different response structures from API
  // API returns: { success: true, data: { items: [], pagination: {} } }
  // Or sometimes directly: { items: [], pagination: {} }
  const controlTypes = Array.isArray(controlTypesData?.items)
    ? controlTypesData.items
    : Array.isArray(controlTypesData)
      ? controlTypesData
      : [];
  const pagination = controlTypesData?.pagination;

  // Calculate stats from actual data
  const stats = {
    totalTypes: pagination?.total || controlTypes.length,
    activeTypes: Array.isArray(controlTypes) ? controlTypes.filter(t => t.isActive).length : 0,
    totalControls: 0, // This would need to be calculated from actual control instances
    pendingUpdates: 0, // This would need backend support
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage + 1 })); // MUI uses 0-based, backend uses 1-based
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    setFilters(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleIssuerChange = (event: SelectChangeEvent<string>) => {
    setFilters(prev => ({ ...prev, issuerId: event.target.value, page: 1 }));
  };

  const handleIncludeInactiveChange = (includeInactive: boolean) => {
    setFilters(prev => ({ ...prev, includeInactive, page: 1 }));
  };

  const handleDeleteControlType = (id: string) => {
    if (window.confirm('Är du säker på att du vill radera denna kontrolltyp?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleEditControlType = (controlType: ControlType) => {
    setSelectedControlType(controlType);
    setIsEditModalOpen(true);
  };

  const handleUpdateControlType = async (id: string, data: UpdateControlTypeRequest): Promise<void> => {
    await updateMutation.mutateAsync({ id, data });
  };

  const handleCloneControlType = (controlType: ControlType) => {
    setSelectedControlType(controlType);
    setIsCloneModalOpen(true);
  };

  const handleSubmitClone = async (id: string, data: CloneControlTypeRequest): Promise<void> => {
    await cloneMutation.mutateAsync({ id, data });
  };

  const handleConfigureForm = (controlType: ControlType) => {
    setSelectedControlType(controlType);
    setIsFormConfigModalOpen(true);
  };

  const handleSaveFormConfiguration = async (
    draft: Omit<SaveFormConfigurationInput, 'controlTypeId'>,
  ): Promise<void> => {
    if (!selectedControlType) return;
    await saveFormConfiguration.mutateAsync({
      controlTypeId: selectedControlType.id,
      ...draft,
    });
  };

  return (
    <Box>
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3
        }}>
        <Typography variant="h4" component="h1">
          Kontrolltyper
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="large"
          onClick={() => setIsCreateModalOpen(true)}
        >
          Skapa ny kontrolltyp
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{
        mb: 4
      }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Totalt antal typer
              </Typography>
              <Typography variant="h4">
                {stats.totalTypes}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Aktiva typer
              </Typography>
              <Typography variant="h4" sx={{
                color: "success.main"
              }}>
                {stats.activeTypes}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Totala kontroller
              </Typography>
              <Typography variant="h4">
                {stats.totalControls}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Väntande uppdateringar
              </Typography>
              <Typography variant="h4" sx={{
                color: "warning.main"
              }}>
                {stats.pendingUpdates}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Control Types Table */}
      <Card>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2
            }}>
            <Typography variant="h6">
              Kontrolltyper
            </Typography>
            <Button
              startIcon={<SettingsIcon />}
              variant="outlined"
              size="small"
            >
              Hantera mallar
            </Button>
          </Box>

          {/* Filters */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 3,
              flexWrap: "wrap"
            }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Utfärdare</InputLabel>
              <Select
                value={filters.issuerId}
                label="Utfärdare"
                onChange={handleIssuerChange}
              >
                <MenuItem value="">Alla</MenuItem>
                {Array.isArray(issuers) && issuers.map(issuer => (
                  <MenuItem key={issuer.id} value={issuer.id}>
                    {issuer.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant={filters.includeInactive ? "contained" : "outlined"}
              onClick={() => handleIncludeInactiveChange(!filters.includeInactive)}
              size="small"
            >
              Visa inaktiva
            </Button>
          </Box>

          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 4
              }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                Laddar kontrolltyper...
              </Typography>
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              Kunde inte ladda kontrolltyper: {error.message}
            </Alert>
          ) : controlTypes.length === 0 ? (
            <Alert severity="info">
              Inga kontrolltyper hittades.
            </Alert>
          ) : (
            <TableContainer 
              component={Paper} 
              variant="outlined"
              sx={{ 
                maxHeight: 'calc(100vh - 400px)', // Adjust based on other content
                overflow: 'auto'
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Namn</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Utfärdare</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Version</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Aktiv från</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Senast uppdaterad</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Åtgärder</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(controlTypes) && controlTypes.map((type) => (
                    <TableRow key={type.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{
                          fontWeight: "medium"
                        }}>
                          {type.name}
                        </Typography>
                        {type.description && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              display: "block"
                            }}>
                            {type.description.length > 50 
                              ? `${type.description.substring(0, 50)}...` 
                              : type.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{
                          fontWeight: "medium"
                        }}>
                          {type.issuer.displayName}
                        </Typography>
                        <Typography variant="caption" sx={{
                          color: "text.secondary"
                        }}>
                          {type.issuer.code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{
                          color: "text.secondary"
                        }}>
                          {type.version}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={type.isActive ? 'Aktiv' : 'Inaktiv'}
                          size="small"
                          color={type.isActive ? 'success' : 'default'}
                          variant={type.isActive ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{
                          color: "text.secondary"
                        }}>
                          {new Date(type.activeFrom).toLocaleDateString('sv-SE')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{
                          color: "text.secondary"
                        }}>
                          {new Date(type.updatedAt).toLocaleDateString('sv-SE')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "center"
                          }}>
                          <Tooltip title="Visa detaljer">
                            <IconButton size="small">
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Konfigurera formulär">
                            <IconButton 
                              size="small" 
                              color="secondary"
                              onClick={() => handleConfigureForm(type)}
                            >
                              <BuildIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Duplicera">
                            <IconButton
                              size="small"
                              onClick={() => handleCloneControlType(type)}
                            >
                              <ContentCopyIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Redigera">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditControlType(type)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ta bort">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteControlType(type.id)}
                                disabled={type.isActive || deleteMutation.isPending}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {/* Pagination */}
          {controlTypesData && pagination && (
            <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 1 }}>
              <TablePagination
                component="div"
                count={pagination.total}
                page={filters.page - 1} // MUI uses 0-based, backend uses 1-based
                onPageChange={handlePageChange}
                rowsPerPage={filters.limit}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rader per sida:"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}–${to} av ${count !== -1 ? count : `mer än ${to}`}`
                }
                sx={{ 
                  '& .MuiTablePagination-selectLabel': { fontSize: '0.875rem' },
                  '& .MuiTablePagination-displayedRows': { fontSize: '0.875rem' }
                }}
              />
              
              {/* Additional pagination info */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, color: 'text.secondary', fontSize: '0.75rem' }}>
                <Typography variant="caption">
                  Sida {pagination.page} av {pagination.totalPages}
                </Typography>
                <Typography variant="caption">
                  {controlTypes.length} av {pagination.total} kontrolltyper visas
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Create Control Type Modal */}
      <CreateControlTypeModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={async (data) => {
          await createMutation.mutateAsync(data);
        }}
        isLoading={createMutation.isPending}
      />

      {/* Edit Control Type Modal */}
      <EditControlTypeModal
        open={isEditModalOpen}
        controlType={selectedControlType}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedControlType(null);
        }}
        onSubmit={handleUpdateControlType}
        isLoading={updateMutation.isPending}
      />

      {/* Clone Control Type Modal */}
      <CloneControlTypeModal
        open={isCloneModalOpen}
        controlType={selectedControlType}
        onClose={() => {
          setIsCloneModalOpen(false);
          setSelectedControlType(null);
        }}
        onSubmit={handleSubmitClone}
        isLoading={cloneMutation.isPending}
      />

      {/* Form Configuration Builder Modal */}
      <FormConfigurationBuilder
        open={isFormConfigModalOpen}
        controlType={selectedControlType as any}
        onClose={() => {
          setIsFormConfigModalOpen(false);
          setSelectedControlType(null);
        }}
        onSave={handleSaveFormConfiguration}
        isLoading={saveFormConfiguration.isPending}
      />
    </Box>
  );
};

export default AdminControlTypesPage;